import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ConnectorType } from '@/types'
import { Separator } from '@/components/ui/separator'
import { useGitHubDeviceFlow } from '@/hooks/useGitHubDeviceFlow'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { CONNECTOR_LABELS, CONNECTOR_TOKEN_LABELS } from './connectorConstants'
import { CopilotDeviceFlowPanel } from './CopilotDeviceFlowPanel'

const TOKEN_HELP_TEXT: Partial<Record<ConnectorType, string>> = {
  GitHub: 'Go to GitHub > Settings > Developer settings > Personal access tokens > Fine-grained tokens, select the repositories you need, and grant Contents (read/write) and Pull requests (write) permissions.',
  GitLab: 'Go to GitLab > Preferences > Access Tokens and create a token with api scope.',
  Bitbucket: 'Go to id.atlassian.com > Security > API tokens and create a token with scopes: read:user:bitbucket, read:workspace:bitbucket, read:repository:bitbucket, write:repository:bitbucket, read:pullrequest:bitbucket, write:pullrequest:bitbucket.',
  AzureDevOps: 'Go to Azure DevOps > User settings > Personal access tokens and create a token with Code (Read) scope.',
  Jira: 'Go to id.atlassian.com > Security > API tokens, create a token with read:jira-user scope, and paste it above along with your Atlassian email.',
  Figma: 'Go to Figma > Settings > Security > Personal access tokens and create a token with at least Current user (read) and File content (read) access.',
  Anthropic: 'Create an API key at console.anthropic.com > API Keys. It is checked with Anthropic when you connect; a key that does not work is not saved.',
  GitHubCopilot: 'Paste a fine-grained personal access token (starts with github_pat_) from an account with an active Copilot subscription. It is checked with GitHub when you connect.',
  QubizTitan: 'Paste the personal key you received from the Titan platform team. Titan can only be reached from your organization\'s run environment, so the key is checked on the first Titan run in this project.',
}

interface FormValues {
  token: string
  email: string
  site: string
}

function CopilotSignIn({ projectId, onComplete }: { projectId: string; onComplete: () => void }) {
  const deviceFlow = useGitHubDeviceFlow(projectId, onComplete)

  return (
    <div className="space-y-4">
      <CopilotDeviceFlowPanel state={deviceFlow.state} onInitiate={deviceFlow.initiate} onReset={deviceFlow.reset} />
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <Separator className="flex-1" />
        <span>or paste a token</span>
        <Separator className="flex-1" />
      </div>
    </div>
  )
}

interface TokenDialogProps {
  projectId: string
  connectorType: ConnectorType | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (token: string, email?: string, site?: string) => void
  isSaving: boolean
  inlineError?: string | null
}

export function TokenDialog({
  projectId,
  connectorType,
  open,
  onOpenChange,
  onSave,
  isSaving,
  inlineError,
}: TokenDialogProps) {
  const isJira = connectorType === 'Jira'
  const isAzureDevOps = connectorType === 'AzureDevOps'
  const isBitbucket = connectorType === 'Bitbucket'
  const needsEmail = isJira || isAzureDevOps || isBitbucket
  const tokenLabel = connectorType ? CONNECTOR_TOKEN_LABELS[connectorType] ?? 'Token' : 'Token'
  const helpText = connectorType ? TOKEN_HELP_TEXT[connectorType] : undefined

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: { token: '', email: '', site: '' },
  })

  useEffect(() => {
    reset({ token: '', email: '', site: '' })
  }, [connectorType, reset])

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset({ token: '', email: '', site: '' })
    onOpenChange(nextOpen)
  }

  const onSubmit = (data: FormValues) => {
    if (isJira) {
      onSave(data.token, data.email, data.site)
    } else if (needsEmail) {
      onSave(data.token, data.email)
    } else {
      onSave(data.token)
    }
  }

  if (!connectorType) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Connect {CONNECTOR_LABELS[connectorType]}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {connectorType === 'GitHubCopilot' && (
            <CopilotSignIn
              projectId={projectId}
              onComplete={() => {
                toast.success('GitHub Copilot connected successfully')
                handleClose(false)
              }}
            />
          )}

          {isAzureDevOps && (
            <div className="space-y-2">
              <Label htmlFor="email">Organization</Label>
              <Input
                id="email"
                data-testid="token-dialog-organization"
                type="text"
                placeholder="my-organization"
                {...register('email', { required: true })}
              />
              <p className="text-sm text-muted-foreground">
                Your Azure DevOps organization name (from dev.azure.com/your-org).
              </p>
            </div>
          )}

          {isBitbucket && (
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="token-dialog-email"
                type="email"
                placeholder="your-email@example.com"
                {...register('email', { required: true })}
              />
              <p className="text-sm text-muted-foreground">
                The email address associated with your Atlassian account.
              </p>
            </div>
          )}

          {isJira && (
            <>
              <div className="space-y-2">
                <Label htmlFor="site">Site</Label>
                <Input
                  id="site"
                  data-testid="token-dialog-site"
                  type="text"
                  placeholder="my-company"
                  {...register('site', { required: true })}
                />
                <p className="text-sm text-muted-foreground">
                  Your Jira site name (from my-company.atlassian.net).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="token-dialog-email"
                  type="email"
                  placeholder="your-email@example.com"
                  {...register('email', { required: true })}
                />
                <p className="text-sm text-muted-foreground">
                  The email address associated with your Atlassian account.
                </p>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="token">{tokenLabel}</Label>
            <Input
              id="token"
              data-testid="token-dialog-token"
              type="password"
              autoComplete="new-password"
              placeholder={`Paste your ${tokenLabel.toLowerCase()}`}
              {...register('token', { required: true })}
            />
            {inlineError && (
              <p role="alert" className="text-sm text-destructive">{inlineError}</p>
            )}
            {helpText && !inlineError && (
              <p className="text-sm text-muted-foreground">{helpText}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !isValid}>
              {isSaving ? 'Connecting...' : 'Connect'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
